import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AutoPayCardMixChargeIssue, CardChargeOutcome, CardChargeUnresolvedOutcome, COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE, COUPON_HOLD_EXPIRED_TITLE } from '@common/models';
import { AnalyticsService, CommerceService, ControlNumbersStatusService, PlansService, SmsService, ToastService } from '@common/services';
import { CONTROL_NUMBERS_FREE_MODE_MESSAGE, CONTROL_NUMBERS_FREE_MODE_TITLE, isControlNumbersFreeModeError } from '@common/utils/control-numbers.util';
import {
    EXTRA_USERS_MIX_RENEWAL_DUE,
    EXTRA_USERS_MIX_RENEWAL_DUE_MESSAGE,
    EXTRA_USERS_SALES_DISABLED_MESSAGE,
    EXTRA_USERS_SALES_DISABLED_TITLE,
    isExtraUsersSalesDisabledError,
} from '@common/utils/extra-users.util';
import { FISCAL_QUOTA_REJECTED, fiscalQuotaIssue, FiscalQuotaIssueCode } from '@common/utils/fiscal-quota.util';
import { loadStripe, Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

/**
 * Detalle del cobro confirmado. `pendingReconciliation: true` (ADM-732): el cobro ya ocurrió y
 * la acreditación se concilia aparte; es éxito, no rechazo, pero el contenedor no debe decir
 * «acreditado».
 */
export interface AutoPayCardPaidEvent {
    pendingReconciliation: boolean;
    message?: string;
}

/**
 * Gestión de tarjeta y cobro automático (Stripe) dentro del flujo de pago del
 * plan o compra de SMS. Solo se muestra a tenants jurídicos (`commerce.plus === true`).
 *
 * Flujo de alta:
 *   1) `createCardSetupIntent$()` -> clientSecret
 *   2) Stripe Payment Element (captura la tarjeta, SCA/3DS incluido)
 *   3) `confirmSetup({ redirect: 'if_required' })` -> setupIntentId
 *   4) `saveCard$(setupIntentId)` -> habilita el cobro automático
 *
 * La clave publicable vive en `environment.STRIPE_PUBLISHABLE_KEY`. La tarjeta
 * nunca toca nuestros servidores: solo se guardan metadatos NO sensibles.
 */
@Component({
    selector: 'fina-auto-pay-card',
    templateUrl: './auto-pay-card.component.html',
    styleUrls: ['./auto-pay-card.component.scss'],
})
export class AutoPayCardComponent implements OnInit, OnDestroy {
    @ViewChild('paymentElementRef') paymentElementRef?: ElementRef<HTMLDivElement>;

    /** Factura pendiente a cobrar con "Pagar con mi tarjeta" (on-session). */
    @Input() billPlanId: string | null = null;
    /** Monto a mostrar en el botón (referencial; el backend cobra el de la factura). */
    @Input() amount: number | null = null;
    /** Cambio de plan opcional; el backend prepara la factura efectiva antes de cobrar. */
    @Input() planType: string | null = null;
    /** Cambio de frecuencia opcional (mensual/trimestral/semestral/anual). */
    @Input() paymentFrequency: string | null = null;
    /** Extras a cobrar en esta factura; el tenant no cambia hasta `pago`. */
    @Input() pendingPurchasedUsers: number | null = null;
    /** Usuarios cuyo cupo se liberó; se bloquean al pasar la factura a `pago`. */
    @Input() pendingBlockUserIds: string[] | null = null;
    /** Cupo fiscal extra pedido (ADM-740). `null` = sin intención: la clave no viaja. */
    @Input() extraControlNumbers: number | null = null;
    /**
     * Compra de paquete SMS: cantidad del catálogo. Si está set, `payNow` cobra
     * vía `/sms/buy-with-card` (create-and-charge) en lugar de una factura de plan.
     */
    @Input() smsQuantity: number | null = null;
    @Input() mixSmsQuantity: number | null = null;
    @Input() mixExtraUsersToAdd: number | null = null;
    @Input() mixIncludeSetup = false;
    /** `quantity` del paquete de números de control del carrito mix (ADM-732). */
    @Input() mixControlNumbers: number | null = null;
    /**
     * Abre automáticamente el formulario de tarjeta (Payment Element) al cargar si
     * el tenant aún no tiene una tarjeta activa. Se usa cuando la tarjeta es un
     * método de pago ya seleccionado (evita un segundo clic en "Activar").
     */
    @Input() autoOpenForm = false;
    /**
     * Modo "solo gestión": pensado para una modal dedicada de administrar tarjeta
     * (eliminar/reemplazar). Oculta el encabezado interno y muestra el botón
     * "Reemplazar tarjeta"; nunca cobra (no se pasa `billPlanId`).
     */
    @Input() manageOnly = false;
    /**
     * Gate opcional justo antes de cobrar. Si emite `false`, no se llama a
     * `pay-with-card`. El checkout de plan lo usa para sellar el cupón: este
     * componente no ve el canje y cobra el monto de la factura.
     */
    @Input() beforePay?: () => Observable<boolean>;

    /** Emite cuando el pago on-session queda confirmado (factura promovida o conciliación pendiente). */
    @Output() paid = new EventEmitter<AutoPayCardPaidEvent>();
    /**
     * Emite cuando el cobro NO cerró en línea: quedó en vuelo (`processing`) o sin
     * confirmar (`unknown`). No es éxito ni rechazo, así que el contenedor no debe
     * mostrar el paso de éxito; solo refrescar el estado real y ofrecer una salida.
     */
    @Output() chargeUnresolved = new EventEmitter<CardChargeUnresolvedOutcome>();
    /**
     * 409 `Cupón reservado expirado`: el servidor ya unsealó. El checkout vuelve
     * al paso 1 y pinta el message en el campo; no es un rechazo de tarjeta.
     */
    @Output() couponHoldExpired = new EventEmitter<string>();
    /** Cobro con tarjeta falló (después del toast), salvo hold expirado / unresolved. */
    @Output() chargeFailed = new EventEmitter<void>();
    /**
     * Error del request de `payMixWithCard` que no es un rechazo de tarjeta (renovación
     * vencida u otro fallo): el contenedor lo registra con el carrito mix completo.
     * Nunca va acompañado de `chargeFailed`.
     */
    @Output() mixChargeIssue = new EventEmitter<AutoPayCardMixChargeIssue>();
    /**
     * Error del cupo fiscal (ADM-740): uno de los cuatro 409 o un 400 que rechaza la cantidad
     * pedida (`FISCAL_QUOTA_REJECTED`). No es un rechazo de tarjeta: nunca va con `chargeFailed`.
     * Los efectos (refrescar el bundle, volver al paso 1, analytics) los hace el contenedor.
     */
    @Output() fiscalQuotaConflict = new EventEmitter<FiscalQuotaIssueCode>();

    private readonly destroy$ = new Subject<void>();
    /** Dispara la (re)carga del estado de la tarjeta; `switchMap` cancela el request
     *  anterior en vuelo para que una respuesta vieja no sobreescriba a una nueva. */
    private readonly loadCard$ = new Subject<void>();
    /** Se pone en `true` en `ngOnDestroy`; evita continuar flujos async (3DS) que
     *  resolvieron una Promise después de destruir el componente. */
    private destroyed = false;

    /** Elegible = tenant jurídico (plus). Si no, el componente no renderiza. */
    eligible = false;

    loading = false;
    saving = false;
    removing = false;
    paying = false;

    /** Resumen del cobro automático (metadatos NO sensibles). */
    autoCharge: {
        enabled: boolean;
        status: string | null;
        hasCard: boolean;
        card: { brand: string | null; last4: string | null; expMonth: number | null; expYear: number | null } | null;
    } | null = null;

    /** Muestra el formulario del Payment Element para capturar una tarjeta nueva. */
    showCardForm = false;
    errorMessage = '';

    /**
     * El cobro de esta factura quedó SIN CONFIRMAR: el procesador no respondió
     * (`outcome: 'unknown'`) o el reintento ya no puede protegerse contra un doble
     * cobro (409 "Cobro sin confirmar"). El cargo pudo entrar y se concilia por
     * webhook, así que no ofrecemos reintentar con la tarjeta. No se resetea: dura
     * lo que dure el componente (la factura necesita revisión antes de recobrar).
     */
    chargeUnconfirmed = false;

    /** Evita reabrir el formulario en cada recarga (solo la primera vez). */
    private autoOpened = false;

    private stripe: Stripe | null = null;
    private elements: StripeElements | null = null;
    private paymentElement: StripePaymentElement | null = null;

    constructor(
        public plansService: PlansService,
        private readonly smsService: SmsService,
        private readonly toastService: ToastService,
        public commerceService: CommerceService,
        private readonly analyticsService: AnalyticsService,
        private readonly controlNumbersStatus: ControlNumbersStatusService,
    ) {}

    ngOnInit(): void {
        // Pipeline único de carga de la tarjeta. `switchMap` cancela el request
        // anterior si `loadCard()` se dispara de nuevo (flags tardíos, tras pagar,
        // tras eliminar), evitando que una respuesta vieja pise a una más reciente.
        this.loadCard$
            .pipe(
                tap(() => (this.loading = true)),
                switchMap(() =>
                    this.plansService.getCard$().pipe(
                        map((res: any) => res?.data ?? null),
                        // No bloquear el flujo si falla: tratamos el error como "sin estado".
                        catchError(() => of(null)),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(data => {
                this.autoCharge = data;
                this.loading = false;
                this.maybeAutoOpenForm();
            });

        this.computeEligibility();
        // Ya no hay flag que esperar, pero este disparo tardío relee `commerce.plus`
        // si el comercio cargó después de `ngOnInit`.
        this.analyticsService.flagsUpdated$?.pipe(takeUntil(this.destroy$)).subscribe(() => this.computeEligibility());
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        this.loadCard$.complete();
        this.destroyPaymentElement();
    }

    private computeEligibility(): void {
        const wasEligible = this.eligible;
        this.eligible = this.commerceService.commerce?.plus === true;
        if (this.eligible && !wasEligible && !this.autoCharge) {
            this.loadCard();
        }
    }

    /** Carga el estado actual del cobro automático del tenant. */
    loadCard(): void {
        // Emite al pipeline de `ngOnInit` (switchMap + catchError + takeUntil).
        this.loadCard$.next();
    }

    /**
     * Abre el formulario de captura de tarjeta una sola vez cuando la tarjeta es el
     * método seleccionado y el tenant aún no tiene una tarjeta activa.
     */
    private maybeAutoOpenForm(): void {
        if (this.autoOpenForm && !this.autoOpened && !this.hasActiveCard && !this.showCardForm && !this.saving) {
            this.autoOpened = true;
            this.startAddCard();
        }
    }

    get hasActiveCard(): boolean {
        return Boolean(this.autoCharge?.hasCard && this.autoCharge?.enabled);
    }

    /**
     * La tarjeta dio un rechazo DEFINITIVO (perdida/robada/vencida/datos
     * inválidos…): no sirve reintentar; hay que reemplazarla.
     */
    get needsCardUpdate(): boolean {
        return this.autoCharge?.status === 'requires_card_update';
    }

    get cardLabel(): string {
        const c = this.autoCharge?.card;
        if (!c?.last4) return '';
        const brand = (c.brand || 'tarjeta').toUpperCase();
        const exp = c.expMonth && c.expYear ? ` · ${String(c.expMonth).padStart(2, '0')}/${c.expYear}` : '';
        return `${brand} •••• ${c.last4}${exp}`;
    }

    /** Marca + últimos 4, p. ej. "VISA •••• 3184". */
    get cardBrandLast4(): string {
        const c = this.autoCharge?.card;
        if (!c?.last4) return '';
        const brand = (c.brand || 'tarjeta').toUpperCase();
        return `${brand} •••• ${c.last4}`;
    }

    /** Vencimiento "MM/AAAA" o cadena vacía. */
    get cardExpiry(): string {
        const c = this.autoCharge?.card;
        if (!c?.expMonth || !c?.expYear) return '';
        return `${String(c.expMonth).padStart(2, '0')}/${c.expYear}`;
    }

    /** Icono Font Awesome específico de la marca (fallback: tarjeta genérica). */
    get cardBrandIcon(): [string, string] {
        const brand = (this.autoCharge?.card?.brand || '').toLowerCase();
        const map: Record<string, string> = {
            visa: 'cc-visa',
            mastercard: 'cc-mastercard',
            amex: 'cc-amex',
            'american express': 'cc-amex',
            discover: 'cc-discover',
            diners: 'cc-diners-club',
            'diners club': 'cc-diners-club',
            jcb: 'cc-jcb',
        };
        const icon = map[brand];
        return icon ? ['fab', icon] : ['far', 'credit-card'];
    }

    /**
     * Solo se puede pagar ya si hay tarjeta activa y un objetivo de cobro
     * (factura de plan pendiente o paquete SMS). Si la tarjeta requiere
     * reemplazo (rechazo definitivo) o el cobro anterior quedó sin confirmar,
     * no ofrecemos pagar con ella.
     */
    get canPayNow(): boolean {
        const hasChargeTarget = Boolean(this.billPlanId) || Boolean(this.smsQuantity) || this.isMixPurchase;
        return this.hasActiveCard && hasChargeTarget && !this.needsCardUpdate && !this.chargeUnconfirmed;
    }

    /** True cuando el cobro es una compra mix de complementos (no factura de plan). */
    get isMixPurchase(): boolean {
        return this.mixSmsQuantity > 0 || this.mixExtraUsersToAdd > 0 || this.mixIncludeSetup || Number(this.mixControlNumbers) > 0;
    }

    get isSmsPurchase(): boolean {
        return Number(this.smsQuantity) > 0 && !this.isMixPurchase;
    }

    /** Carga (memoizado) el cliente de Stripe para resolver el 3DS on-session. */
    private async ensureStripe(): Promise<Stripe | null> {
        if (this.stripe) return this.stripe;
        if (!environment.STRIPE_PUBLISHABLE_KEY) return null;
        this.stripe = await loadStripe(environment.STRIPE_PUBLISHABLE_KEY);
        return this.stripe;
    }

    /**
     * Pago on-session con la tarjeta guardada. Cobra la factura pendiente (o el
     * paquete SMS) y, si el banco pide 3DS, resuelve la autenticación en el
     * browser con `handleNextAction` y luego concilia con `confirmCardPayment$`.
     * Si hay `planType`/`paymentFrequency`, el backend crea/actualiza la factura
     * efectiva; usamos `effectiveBillPlanId` para el 3DS y reintentos.
     */
    async payNow(): Promise<void> {
        if (!this.canPayNow || this.paying) return;
        this.paying = true;
        this.errorMessage = '';

        if (this.isSmsPurchase) {
            this.paySmsWithCard();
            return;
        }
        if (this.isMixPurchase) {
            this.payMixWithCard();
            return;
        }

        if (!this.beforePay) {
            this.chargePlanWithCard();
            return;
        }

        this.beforePay()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: allowed => {
                    if (!allowed) {
                        this.paying = false;
                        return;
                    }
                    this.chargePlanWithCard();
                },
                error: () => {
                    this.paying = false;
                },
            });
    }

    private chargePlanWithCard(): void {
        let billPlanId = this.billPlanId as string;
        this.plansService
            .payWithCard$(billPlanId, {
                planType: this.planType,
                paymentFrequency: this.paymentFrequency,
                pendingPurchasedUsers: this.pendingPurchasedUsers,
                pendingBlockUserIds: this.pendingBlockUserIds,
                // Sin intención la clave no se añade: el body (y las aserciones exactas) siguen igual.
                ...(this.extraControlNumbers != null ? { extraControlNumbers: this.extraControlNumbers } : {}),
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async (res: any) => {
                    await this.handlePayOutcome(res, billPlanId, id => {
                        this.billPlanId = id;
                        billPlanId = id;
                    });
                },
                error: (err: any) => {
                    if (this.isUnconfirmedChargeError(err)) {
                        this.markChargeUnconfirmed(err?.error?.title, err?.error?.message);
                        return;
                    }
                    if (isExtraUsersSalesDisabledError(err)) {
                        this.paying = false;
                        this.notifyExtraUsersSalesDisabled(err);
                        return;
                    }
                    this.reportPlanChargeFailure(err, 'No se pudo procesar el pago.');
                },
            });
    }

    /**
     * 409 `EXTRA_USERS_SALES_DISABLED` (ADM-790): la venta de cupos está apagada y no hubo
     * cobro. No es un rechazo de la tarjeta: aviso con el copy del servidor, sin
     * `chargeFailed`, y se refresca el bundle para que el modal recalcule los montos.
     */
    private notifyExtraUsersSalesDisabled(err: any): void {
        this.toastService.alert(err?.error?.title || EXTRA_USERS_SALES_DISABLED_TITLE, err?.error?.message || EXTRA_USERS_SALES_DISABLED_MESSAGE, 8000);
        this.plansService.obtainPlansBundle();
    }

    private payMixWithCard(): void {
        this.plansService
            .payWithCard$(null, {
                paymentType: 'mix',
                sms: this.mixSmsQuantity,
                extraUsersToAdd: this.mixExtraUsersToAdd,
                includeSetup: this.mixIncludeSetup,
                controlNumbers: this.mixControlNumbers,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async (res: any) => {
                    const data = res?.data ?? {};
                    const billPlanId = data.effectiveBillPlanId || data.billPlanId || null;
                    if (billPlanId) {
                        this.billPlanId = billPlanId;
                    }
                    await this.handlePayOutcome(res, billPlanId);
                },
                error: (err: any) => {
                    if (this.isUnconfirmedChargeError(err)) {
                        this.markChargeUnconfirmed(err?.error?.title, err?.error?.message);
                        return;
                    }
                    this.paying = false;
                    // Cupos con la fecha de pago vencida: no es un rechazo de la tarjeta (no hubo
                    // cobro); los cupos se suman en la renovación. Mismo aviso que el pago por banco.
                    if (err?.status === 409 && err?.error?.errorCode === EXTRA_USERS_MIX_RENEWAL_DUE) {
                        this.toastService.alert(err.error.title || 'Renovación pendiente', err.error.message || EXTRA_USERS_MIX_RENEWAL_DUE_MESSAGE, 8000);
                        this.mixChargeIssue.emit({ reason: 'renewal_due', httpStatus: err.status });
                        return;
                    }
                    // Venta de cupos apagada (ADM-790): aviso, no rechazo. Sube como el error de
                    // request que es; el contenedor decide el evento (no hay `reason` nuevo).
                    if (isExtraUsersSalesDisabledError(err)) {
                        this.notifyExtraUsersSalesDisabled(err);
                        this.mixChargeIssue.emit({ reason: 'request_error', httpStatus: err.status });
                        return;
                    }
                    // Solicitud gratuita activa (ADM-827): no hubo cobro. Aviso, recarga del estado (saca
                    // el paquete del carrito de «Mejora tu Fina») y sube como el error de request que es.
                    if (isControlNumbersFreeModeError(err)) {
                        this.toastService.alert(err.error.title || CONTROL_NUMBERS_FREE_MODE_TITLE, err.error.message || CONTROL_NUMBERS_FREE_MODE_MESSAGE, 8000);
                        this.controlNumbersStatus.refresh();
                        this.mixChargeIssue.emit({ reason: 'request_error', httpStatus: err.status });
                        return;
                    }
                    this.toastService.error(err?.error?.title || 'Error', err?.error?.message || 'No se pudo procesar la compra.');
                    this.mixChargeIssue.emit({ reason: 'request_error', httpStatus: typeof err?.status === 'number' ? err.status : null });
                },
            });
    }

    /** Cobro create-and-charge de un paquete SMS con la tarjeta guardada. */
    private paySmsWithCard(): void {
        const sms = Number(this.smsQuantity);
        const amount = Number(this.amount);
        if (!(sms > 0) || !(amount > 0)) {
            this.paying = false;
            this.errorMessage = 'Selecciona un paquete de SMS válido.';
            this.toastService.error('Paquete requerido', this.errorMessage);
            return;
        }

        this.smsService
            .buySmsWithCard$(sms, amount)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async (res: any) => {
                    const data = res?.data ?? {};
                    const billPlanId = data.effectiveBillPlanId || data.billPlanId || null;
                    if (billPlanId) {
                        this.billPlanId = billPlanId;
                    }
                    await this.handlePayOutcome(res, billPlanId);
                },
                error: (err: any) => {
                    if (this.isUnconfirmedChargeError(err)) {
                        this.markChargeUnconfirmed(err?.error?.title, err?.error?.message);
                        return;
                    }
                    this.paying = false;
                    this.toastService.error(err?.error?.title || 'Error', err?.error?.message || 'No se pudo procesar la compra de SMS.');
                },
            });
    }

    private async handlePayOutcome(res: any, billPlanId: string | null, onEffectiveId?: (id: string) => void): Promise<void> {
        const data = res?.data ?? {};
        const outcome: CardChargeOutcome = data.outcome;
        const effectiveBillPlanId = data.effectiveBillPlanId || data.billPlanId || billPlanId;
        if (effectiveBillPlanId && onEffectiveId && effectiveBillPlanId !== billPlanId) {
            onEffectiveId(String(effectiveBillPlanId));
        }

        if (outcome === 'succeeded' || outcome === 'already_processed') {
            this.onPaid(res?.title, res?.message, data.pendingReconciliation === true);
            return;
        }

        if (outcome === 'requires_action') {
            if (!effectiveBillPlanId) {
                this.paying = false;
                this.errorMessage = 'No se pudo iniciar la verificación del banco.';
                this.toastService.error('Error', this.errorMessage);
                this.chargeFailed.emit();
                return;
            }
            await this.resolve3ds(String(effectiveBillPlanId), data.clientSecret, data.intentId);
            return;
        }

        if (outcome === 'processing') {
            this.onChargeProcessing(res?.title, res?.message);
            return;
        }

        if (outcome === 'unknown') {
            this.markChargeUnconfirmed(res?.title, res?.message);
            return;
        }

        this.paying = false;
        this.errorMessage = data.errorMessage || res?.message || 'El pago fue rechazado.';
        this.toastService.error(res?.title || 'Pago rechazado', this.errorMessage);
        this.chargeFailed.emit();
    }

    /**
     * Cobro aceptado pero aún en vuelo (`processing`): NO es un rechazo. Se confirma
     * solo por webhook, así que no hay nada que reintentar ni éxito que celebrar.
     */
    private onChargeProcessing(title?: string, message?: string): void {
        this.paying = false;
        this.errorMessage = '';
        this.toastService.info(title || 'Cobro en proceso', message || 'El cobro quedó en proceso; se confirmará automáticamente.');
        this.loadCard();
        // Último: el contenedor cierra la modal y destruye este componente.
        this.chargeUnresolved.emit('processing');
    }

    /**
     * Cobro SIN CONFIRMAR: el procesador no respondió (`unknown`) o el reintento no
     * puede protegerse contra un doble cobro (409 "Cobro sin confirmar"). El cargo
     * pudo entrar, así que nunca lo llamamos rechazo ni invitamos a reintentar: se
     * bloquea el pago con tarjeta de esta factura y se ofrece otro medio de pago.
     */
    private markChargeUnconfirmed(title?: string, message?: string): void {
        this.paying = false;
        this.chargeUnconfirmed = true;
        this.errorMessage = message || 'No pudimos confirmar el cobro. Revisa el estado antes de reintentar.';
        // `alert` (ámbar, sin autohide): el usuario tiene que leerlo antes de actuar.
        this.toastService.alert(title || 'No pudimos confirmar el cobro', this.errorMessage);
        this.chargeUnresolved.emit('unknown');
    }

    /** 409 del backend cuando el cobro previo quedó sin confirmar y no puede reintentarse. */
    private isUnconfirmedChargeError(err: any): boolean {
        return err?.status === 409 && err?.error?.data?.outcome === 'unknown';
    }

    private isCouponHoldExpiredError(err: any): boolean {
        return err?.error?.title === COUPON_HOLD_EXPIRED_TITLE;
    }

    /**
     * Hold vencido: avisa al checkout. Error del cupo fiscal: su toast + `fiscalQuotaConflict`.
     * Cualquier otro fallo de cobro: toast + chargeFailed.
     */
    private reportPlanChargeFailure(err: any, fallbackMessage: string): void {
        this.paying = false;
        if (this.isCouponHoldExpiredError(err)) {
            this.couponHoldExpired.emit(err?.error?.message || COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE);
            return;
        }
        const fiscalIssue = fiscalQuotaIssue(err, { intentSent: this.extraControlNumbers != null });
        if (fiscalIssue) {
            const title = err?.error?.title || 'Cambio de facturas no disponible';
            const message = err?.error?.message || fallbackMessage;
            if (fiscalIssue === FISCAL_QUOTA_REJECTED) {
                this.toastService.error(title, message);
            } else {
                this.toastService.alert(title, message, 8000);
            }
            this.fiscalQuotaConflict.emit(fiscalIssue);
            return;
        }
        this.toastService.error(err?.error?.title || 'Error', err?.error?.message || fallbackMessage);
        this.chargeFailed.emit();
    }

    /** Completa el 3DS en el browser y concilia el pago con el backend. */
    private async resolve3ds(billPlanId: string, clientSecret?: string, intentId?: string): Promise<void> {
        if (!clientSecret || !intentId) {
            this.paying = false;
            this.errorMessage = 'No se pudo iniciar la verificación del banco.';
            this.toastService.error('Error', this.errorMessage);
            this.chargeFailed.emit();
            return;
        }

        try {
            const stripe = await this.ensureStripe();
            if (!stripe) {
                this.paying = false;
                this.errorMessage = 'El pago con tarjeta no está disponible por ahora.';
                this.toastService.error('Configuración incompleta', this.errorMessage);
                this.chargeFailed.emit();
                return;
            }

            const { error } = await stripe.handleNextAction({ clientSecret });
            // El 3DS puede tardar; si el usuario cerró la modal / navegó fuera mientras
            // tanto, no seguimos. `takeUntil(destroy$)` no basta acá: si el componente ya
            // se destruyó, `destroy$` está completado y una suscripción creada DESPUÉS no
            // se cancela (takeUntil solo corta cuando el notifier EMITE, no al completar).
            if (this.destroyed) {
                return;
            }
            if (error) {
                this.paying = false;
                this.errorMessage = error.message || 'No se pudo completar la verificación del banco.';
                this.toastService.error('Verificación fallida', this.errorMessage);
                this.chargeFailed.emit();
                return;
            }

            this.plansService
                .confirmCardPayment$(billPlanId, intentId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (res: any) => {
                        // No delegamos en `handlePayOutcome`: la confirmación puede volver
                        // en `requires_action` ("el pago aún requiere autenticación") y
                        // reentraríamos en un bucle de 3DS.
                        const outcome: CardChargeOutcome = res?.data?.outcome;
                        if (outcome === 'succeeded' || outcome === 'already_processed') {
                            this.onPaid(res?.title, res?.message, res?.data?.pendingReconciliation === true);
                            return;
                        }
                        if (outcome === 'processing') {
                            this.onChargeProcessing(res?.title, res?.message);
                            return;
                        }
                        if (outcome === 'unknown') {
                            this.markChargeUnconfirmed(res?.title, res?.message);
                            return;
                        }
                        this.paying = false;
                        this.errorMessage = res?.data?.errorMessage || res?.message || 'El pago no pudo completarse.';
                        this.toastService.error(res?.title || 'Pago no completado', this.errorMessage);
                        this.chargeFailed.emit();
                    },
                    error: (err: any) => {
                        if (this.isUnconfirmedChargeError(err)) {
                            this.markChargeUnconfirmed(err?.error?.title, err?.error?.message);
                            return;
                        }
                        this.reportPlanChargeFailure(err, 'No se pudo confirmar el pago.');
                    },
                });
        } catch {
            if (this.destroyed) {
                return;
            }
            this.paying = false;
            this.errorMessage = 'No se pudo completar la verificación del banco.';
            this.toastService.error('Verificación fallida', this.errorMessage);
            this.chargeFailed.emit();
        }
    }

    /** `pendingReconciliation`: el cobro ocurrió y se concilia aparte; sigue siendo un éxito (toast de éxito + `paid`). */
    private onPaid(title?: string, message?: string, pendingReconciliation = false): void {
        this.paying = false;
        this.analyticsService.analyticsCapture(this.isSmsPurchase ? 'Compra SMS con tarjeta' : 'Pago con tarjeta guardada');
        this.toastService.success(
            title || 'Pago exitoso',
            message || (this.isSmsPurchase ? 'Tu paquete de SMS fue pagado con la tarjeta guardada.' : 'Tu factura fue pagada con la tarjeta guardada.'),
        );
        this.loadCard();
        this.paid.emit({ pendingReconciliation, message });
    }

    /** Paso 1-2: crea el SetupIntent y monta el Payment Element de Stripe. */
    async startAddCard(): Promise<void> {
        this.errorMessage = '';
        if (!environment.STRIPE_PUBLISHABLE_KEY) {
            this.errorMessage = 'El pago con tarjeta no está disponible por ahora.';
            this.toastService.error('Configuración incompleta', this.errorMessage);
            return;
        }
        this.saving = true;
        this.plansService
            .createCardSetupIntent$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async (res: any) => {
                    const clientSecret = res?.data?.clientSecret;
                    if (!clientSecret) {
                        this.saving = false;
                        this.errorMessage = 'No se pudo iniciar el registro de la tarjeta.';
                        this.toastService.error('Error', this.errorMessage);
                        return;
                    }
                    try {
                        this.stripe = await this.ensureStripe();
                        if (!this.stripe) throw new Error('No se pudo cargar Stripe');
                        this.elements = this.stripe.elements({ clientSecret, locale: 'es' });
                        this.paymentElement = this.elements.create('payment');
                        this.showCardForm = true;
                        // Esperar a que *ngIf renderice el contenedor antes de montar.
                        setTimeout(() => {
                            if (this.paymentElementRef?.nativeElement && this.paymentElement) {
                                this.paymentElement.mount(this.paymentElementRef.nativeElement);
                            }
                            this.saving = false;
                        });
                    } catch (err) {
                        this.saving = false;
                        this.errorMessage = 'No se pudo cargar el formulario de tarjeta.';
                        this.toastService.error('Error', this.errorMessage);
                    }
                },
                error: (err: any) => {
                    this.saving = false;
                    this.errorMessage = err?.error?.message || 'No se pudo iniciar el registro de la tarjeta.';
                    this.toastService.error(err?.error?.title || 'Error', this.errorMessage);
                },
            });
    }

    /** Paso 3-4: confirma la tarjeta con Stripe y la guarda en el backend. */
    async confirmAddCard(): Promise<void> {
        if (!this.stripe || !this.elements) return;
        this.saving = true;
        this.errorMessage = '';
        try {
            const { error, setupIntent } = await this.stripe.confirmSetup({
                elements: this.elements,
                redirect: 'if_required',
            });

            if (error) {
                this.errorMessage = error.message || 'No se pudo confirmar la tarjeta.';
                this.saving = false;
                return;
            }

            const setupIntentId = setupIntent?.id;
            if (!setupIntentId) {
                this.errorMessage = 'No se pudo confirmar la tarjeta.';
                this.saving = false;
                return;
            }

            this.plansService
                .saveCard$(setupIntentId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (res: any) => {
                        this.autoCharge = res?.data ?? this.autoCharge;
                        this.analyticsService.analyticsCapture('Tarjeta guardada (cobro automático)');
                        this.toastService.success(res?.title || 'Listo', res?.message || 'Cobro automático activado.');
                        this.closeCardForm();
                        this.saving = false;
                        this.loadCard();
                    },
                    error: (err: any) => {
                        this.saving = false;
                        this.toastService.error(err?.error?.title || 'Error', err?.error?.message || 'No se pudo guardar la tarjeta.');
                    },
                });
        } catch (err) {
            this.saving = false;
            this.errorMessage = 'No se pudo confirmar la tarjeta.';
            // El mensaje inline vive dentro del formulario y puede quedar fuera de
            // vista; mostramos también el toast global como el resto de los errores.
            this.toastService.error('Error', this.errorMessage);
        }
    }

    /** Elimina la tarjeta y desactiva el cobro automático. */
    removeCard(): void {
        this.removing = true;
        this.plansService
            .deleteCard$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.autoCharge = res?.data ?? { enabled: false, status: null, hasCard: false, card: null };
                    this.analyticsService.analyticsCapture('Tarjeta eliminada (cobro automático)');
                    this.toastService.success(res?.title || 'Listo', res?.message || 'Cobro automático desactivado.');
                    this.removing = false;
                    // Sin tarjeta y con la tarjeta como método elegido: reabrir el formulario
                    // para capturar una nueva y no dejar el panel vacío sin acción.
                    if (this.autoOpenForm) {
                        this.autoOpened = false;
                        this.maybeAutoOpenForm();
                    }
                },
                error: (err: any) => {
                    this.removing = false;
                    this.toastService.error(err?.error?.title || 'Error', err?.error?.message || 'No se pudo eliminar la tarjeta.');
                },
            });
    }

    closeCardForm(): void {
        this.showCardForm = false;
        this.destroyPaymentElement();
    }

    private destroyPaymentElement(): void {
        try {
            this.paymentElement?.destroy();
        } catch {
            // Ignorar: el elemento pudo no haberse montado.
        }
        this.paymentElement = null;
        this.elements = null;
    }
}
